/**
 * Cart Upsell Slider Manager
 * Handles initialization and re-initialization of the cart upsell slider
 */

class CartUpsellSliderManager {
  constructor() {
    this.sliderInstance = null;
    this.isInitialized = false;
    this.initTimeout = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    
    this.initSlider = this.initSlider.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);
    this.handleCartDrawerRender = this.handleCartDrawerRender.bind(this);
    this.handleUpsellSubmit = this.handleUpsellSubmit.bind(this);
    
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.initSlider);
    } else {
      this.initSlider();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
    }

    document.addEventListener('cart:refresh', this.handleCartRefresh);
    document.addEventListener('cart-drawer:render-contents', this.handleCartDrawerRender);
    this.setupVisibilityObserver();
    this.setupUpsellATCDelegation();
  }

  initSlider() {
  this.clearTimeout();

  const cartDrawer = document.querySelector('cart-drawer');
  const scope = cartDrawer || document;

  const sliderElement = scope.querySelector('.cart-upsell__products.blaze-slider');
  if (!sliderElement) return;

  if (typeof BlazeSlider === 'undefined') {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.scheduleInit(100 * this.retryCount);
    }
    return;
  }

  this.createSlider(sliderElement);
}


  createSlider(element) {
    this.destroySlider();

    try {
      this.sliderInstance = new BlazeSlider(element, {
        all: {
          slidesToShow: 1,
          slideGap: '1rem',
          loop: true,
          enableAutoplay: false,
          draggable: true,
          transitionDuration: 300,
        }
      });

      this.isInitialized = true;
      this.retryCount = 0;
      element.blazeSliderInitialized = true;
    } catch (error) {
      console.warn('Cart upsell slider initialization failed:', error);
      this.scheduleInit(500);
    }
  }

  destroySlider() {
    if (this.sliderInstance?.destroy) {
      try {
        this.sliderInstance.destroy();
      } catch (error) {
        console.warn('Error destroying slider:', error);
      }
    }
    this.sliderInstance = null;
    this.isInitialized = false;
  }

  scheduleInit(delay = 100) {
    this.clearTimeout();
    this.initTimeout = setTimeout(this.initSlider, delay);
  }

  clearTimeout() {
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
      this.initTimeout = null;
    }
  }

  handleCartUpdate(event) {
    const delay = event.source === 'product-form' ? 200 : 100;
    this.scheduleInit(delay);
  }

  handleCartRefresh() {
    this.scheduleInit(150);
  }

  handleCartDrawerRender() {
    this.scheduleInit(100);
  }

  setupVisibilityObserver() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  // 1) When drawer becomes active/open, try init
  const classObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.attributeName === 'class') {
        if (cartDrawer.classList.contains('active')) {
          this.scheduleInit(50);
        }
      }
    }
  });

  classObserver.observe(cartDrawer, {
    attributes: true,
    attributeFilter: ['class']
  });

  // 2) When drawer HTML updates (nodes added/removed), try init
  // Pick the container that actually gets re-rendered in your theme:
  const drawerInner =
    cartDrawer.querySelector('#CartDrawer') ||
    cartDrawer.querySelector('.drawer__inner') ||
    cartDrawer;

  const domObserver = new MutationObserver(() => {
    // If the slider is injected later, this catches it
    const sliderEl = drawerInner.querySelector('.cart-upsell__products.blaze-slider');
    if (sliderEl) {
      this.scheduleInit(0);
    }
  });

  domObserver.observe(drawerInner, {
    childList: true,
    subtree: true
  });

  // store refs if you ever want to disconnect them in destroy()
  this._classObserver = classObserver;
  this._domObserver = domObserver;
}

setupUpsellATCDelegation() {
  const cartDrawer = document.querySelector('cart-drawer');
  const scope = cartDrawer || document;

  if (this._upsellDelegated) return;
  this._upsellDelegated = true;

  // Capture submit events from injected upsell forms
  scope.addEventListener('submit', this.handleUpsellSubmit, true);
}

async handleUpsellSubmit(e) {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;

  // Only intercept UPSell add-to-cart forms
  if (!form.closest('.cart-upsell')) return;
  if (form.action !== `${window.location.origin}/cart/add`) return;

  e.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
  }

  try {
    const formData = new FormData(form);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    if (!res.ok) {
      const txt = await res.text();
      console.warn('Upsell add failed:', txt);
      return;
    }

    // Tell Dawn to refresh the drawer
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'upsell' });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  } catch (err) {
    console.warn('Upsell add-to-cart error:', err);
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
  }
}



  reinitialize() {
    this.scheduleInit(0);
  }

  destroy() {
  this.clearTimeout();
  this.destroySlider();
  this._classObserver?.disconnect();
  this._domObserver?.disconnect();
}

}

if (!window.cartUpsellSliderManager) {
  window.cartUpsellSliderManager = new CartUpsellSliderManager();
}

window.CartUpsellSliderManager = CartUpsellSliderManager;